/**
 * AuthModal - Login/Signup modal with fun design
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FONT_FAMILY } from '../ShopStyles';
import { useAuth } from '../context/AuthContext';

const AuthModal = () => {
  const { isLoginModalOpen, closeLoginModal, login, signup, loginWithDemo } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isLoginModalOpen) {
      setFormData({ name: '', email: '', password: '' });
      setError('');
      setMode('login');
    }
  }, [isLoginModalOpen]);

  // Inject animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'auth-modal-animations';
    styleSheet.textContent = `
      @keyframes modalSlideIn {
        from { opacity: 0; transform: scale(0.95) translateY(20px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes overlayFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .auth-modal-overlay {
        animation: overlayFadeIn 0.2s ease-out;
      }
      .auth-modal-content {
        animation: modalSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('auth-modal-animations');
      if (existing) existing.remove();
    };
  }, []);

  if (!isLoginModalOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'login') {
      if (!formData.email || !formData.password) {
        setError('Please fill in all fields');
        return;
      }
      login(formData.email, formData.password);
    } else {
      if (!formData.name || !formData.email || !formData.password) {
        setError('Please fill in all fields');
        return;
      }
      signup(formData.name, formData.email, formData.password);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const modal = (
    <>
      {/* Overlay */}
      <div 
        className="auth-modal-overlay" 
        style={styles.overlay} 
        onClick={closeLoginModal} 
      />
      
      {/* Modal */}
      <div className="auth-modal-content" style={styles.modal}>
        {/* Close button */}
        <button onClick={closeLoginModal} style={styles.closeBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerEmoji}>🐾</span>
          <h2 style={styles.title}>
            {mode === 'login' ? 'Welcome Back!' : 'Join PetJR!'}
          </h2>
          <p style={styles.subtitle}>
            {mode === 'login' 
              ? 'Sign in to access your portraits and orders'
              : 'Create an account to save your pet portraits'
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'signup' && (
            <div style={styles.field}>
              <label style={styles.label}>Your Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Sarah Johnson"
                style={styles.input}
              />
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@email.com"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              style={styles.input}
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.submitBtn}>
            {mode === 'login' ? '🚀 Sign In' : '✨ Create Account'}
          </button>
        </form>

        {/* Divider */}
        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <span style={styles.dividerLine} />
        </div>

        {/* Demo login */}
        <button onClick={loginWithDemo} style={styles.demoBtn}>
          👤 Continue as Demo User
        </button>

        {/* Toggle mode */}
        <p style={styles.toggleText}>
          {mode === 'login' ? (
            <>
              Don't have an account?{' '}
              <button onClick={() => setMode('signup')} style={styles.toggleBtn}>
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button onClick={() => setMode('login')} style={styles.toggleBtn}>
                Sign in
              </button>
            </>
          )}
        </p>

        {/* Trust badges */}
        <div style={styles.trustBadges}>
          <span>🔒 Secure</span>
          <span>•</span>
          <span>🎨 Free to create</span>
          <span>•</span>
          <span>💜 No spam</span>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(8px)',
    zIndex: 10000,
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '420px',
    maxWidth: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: '24px',
    padding: '32px',
    zIndex: 10001,
    boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    background: '#f3f4f6',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  headerEmoji: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '12px',
  },
  title: {
    fontSize: '26px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  input: {
    padding: '14px 16px',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  error: {
    fontSize: '14px',
    color: '#dc2626',
    fontFamily: FONT_FAMILY,
    margin: 0,
    padding: '8px 12px',
    background: '#fef2f2',
    borderRadius: '8px',
  },
  submitBtn: {
    padding: '16px',
    fontSize: '17px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '14px',
    color: '#fff',
    cursor: 'pointer',
    marginTop: '8px',
    boxShadow: '0 4px 16px rgba(147, 51, 234, 0.3)',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '24px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#e5e7eb',
  },
  dividerText: {
    fontSize: '13px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
  },
  demoBtn: {
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#f3f4f6',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    color: '#374151',
    cursor: 'pointer',
  },
  toggleText: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: '20px 0 0',
  },
  toggleBtn: {
    background: 'none',
    border: 'none',
    color: '#7c3aed',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: FONT_FAMILY,
    padding: 0,
  },
  trustBadges: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #f3f4f6',
    fontSize: '12px',
    color: '#9ca3af',
    fontFamily: FONT_FAMILY,
  },
};

export default AuthModal;
